import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Globe, RefreshCw } from 'lucide-react';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { API_BASE } from '../lib/apiBase';

const API = `${API_BASE}/api`;

/**
 * Reference Data → Countries admin UI.
 * Searchable list of the single Countries master: display name, immutable ISO
 * code, and an active/inactive toggle. Inactive countries stay valid for
 * historical records but are unavailable for new selections.
 */
export default function CountriesManager() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [onlyActive, setOnlyActive] = useState(false);
    const [savingCode, setSavingCode] = useState(null);

    const authHeaders = () => {
        const token = localStorage.getItem('token');
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    const fetchCountries = async () => {
        try {
            setLoading(true);
            const r = await axios.get(`${API}/admin/countries`, authHeaders());
            setCountries(r.data.countries || []);
        } catch (e) {
            toast.error('Failed to load countries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCountries(); }, []);

    const toggleActive = async (country, next) => {
        setSavingCode(country.code);
        // optimistic update
        setCountries((prev) => prev.map((c) => (c.code === country.code ? { ...c, active: next } : c)));
        try {
            await axios.patch(`${API}/admin/countries/${country.code}`, { active: next }, authHeaders());
            toast.success(`${country.name} ${next ? 'activated' : 'deactivated'}`);
        } catch (e) {
            // revert
            setCountries((prev) => prev.map((c) => (c.code === country.code ? { ...c, active: !next } : c)));
            toast.error(`Failed to update ${country.name}`);
        } finally {
            setSavingCode(null);
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return countries.filter((c) => {
            if (onlyActive && !c.active) return false;
            if (!q) return true;
            return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
        });
    }, [countries, search, onlyActive]);

    const activeCount = countries.filter((c) => c.active).length;

    return (
        <Card data-testid="countries-manager">
            <CardHeader>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Globe className="h-5 w-5 text-slate-500" />
                            Countries
                        </CardTitle>
                        <CardDescription>
                            The single master of countries (ISO 3166-1 alpha-2) used by every country
                            selector across the platform. Inactive countries remain valid for historical
                            records but cannot be chosen for new entries.
                        </CardDescription>
                    </div>
                    <Badge variant="outline" className="shrink-0">{activeCount} active · {countries.length} total</Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name or ISO code..."
                            className="pl-8"
                            data-testid="countries-search"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
                        <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="countries-only-active" />
                        Active only
                    </label>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="border rounded-md max-h-[520px] overflow-auto">
                        <Table>
                            <TableHeader className="sticky top-0 bg-white z-10">
                                <TableRow>
                                    <TableHead>Country</TableHead>
                                    <TableHead className="w-[120px]">ISO code</TableHead>
                                    <TableHead className="w-[140px] text-right">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((c) => (
                                    <TableRow key={c.code} data-testid={`country-row-${c.code}`}>
                                        <TableCell className="font-medium">{c.name}</TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="font-mono">{c.code}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <span className={`text-xs ${c.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                    {c.active ? 'Active' : 'Inactive'}
                                                </span>
                                                <Switch
                                                    checked={c.active}
                                                    disabled={savingCode === c.code}
                                                    onCheckedChange={(v) => toggleActive(c, v)}
                                                    data-testid={`country-toggle-${c.code}`}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center text-slate-400 py-8">
                                            No countries match your search.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
